/* Clinician testimonials: circle previews, horizontal slider, popup player.
   Note: scrollTo({behavior:'smooth'}) is cancelled by scroll-snap-type: x mandatory
   in current Chrome and snaps back to 0, so scrollLeft is animated manually. */
(function () {
  'use strict';

  var modal, video, lastFocus;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function saveData() {
    var c = navigator.connection;
    return !!(c && (c.saveData || /2g/.test(c.effectiveType || '')));
  }

  /* ---------- muted circle previews ---------- */

  function initPreviews(root) {
    var previews = root.querySelectorAll('[data-rtq-preview]');
    if (!previews.length) return;

    // Leave the poster in place when motion is unwanted or data is precious.
    if (prefersReducedMotion() || saveData()) return;

    if (!('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (entry.isIntersecting) {
          if (!v.getAttribute('src')) {
            v.setAttribute('src', v.getAttribute('data-src'));
          }
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else if (!v.paused) {
          v.pause();
        }
      });
    }, { threshold: 0.4 });

    previews.forEach(function (v) {
      v.muted = true;
      io.observe(v);
    });
  }

  function pausePreviews() {
    document.querySelectorAll('[data-rtq-preview]').forEach(function (v) {
      if (!v.paused) v.pause();
    });
  }

  function resumePreviews() {
    if (prefersReducedMotion() || saveData()) return;
    document.querySelectorAll('[data-rtq-preview]').forEach(function (v) {
      if (v.getAttribute('src')) {
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      }
    });
  }

  /* ---------- popup player ---------- */

  function buildModal() {
    if (modal) return;
    modal = document.createElement('div');
    modal.className = 'rtq-cl-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.innerHTML =
      '<button type="button" class="rtq-cl-modal__close" aria-label="Close">&times;</button>' +
      '<video controls playsinline preload="none"></video>';
    document.body.appendChild(modal);
    video = modal.querySelector('video');
    modal.querySelector('.rtq-cl-modal__close').addEventListener('click', closeModal);
    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeModal();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });
  }

  function openModal(src, label) {
    if (!src) return;
    buildModal();
    pausePreviews();
    lastFocus = document.activeElement;
    video.src = src;
    modal.setAttribute('aria-label', label ? 'Video testimonial from ' + label : 'Video testimonial');
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    modal.querySelector('.rtq-cl-modal__close').focus();
    var playing = video.play();
    if (playing && playing.catch) playing.catch(function () {});
  }

  function closeModal() {
    if (!modal) return;
    modal.classList.remove('is-open');
    document.body.style.overflow = '';
    video.pause();
    video.removeAttribute('src');
    video.load();
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    resumePreviews();
  }

  /* ---------- slider ---------- */

  function initSlider(root) {
    var row = root.querySelector('[data-rtq-row]');
    if (!row) return;

    var prev = root.querySelector('[data-rtq-prev]');
    var next = root.querySelector('[data-rtq-next]');
    var dotsWrap = root.querySelector('[data-rtq-dots]');
    var cards = Array.prototype.slice.call(row.children);
    if (!cards.length) return;

    var raf;
    var dots = [];

    function stops() {
      var max = row.scrollWidth - row.clientWidth;
      return cards.map(function (card) {
        return Math.max(0, Math.min(max, card.offsetLeft - cards[0].offsetLeft));
      });
    }

    function currentIndex() {
      var s = stops();
      var best = 0;
      for (var i = 0; i < s.length; i++) {
        if (Math.abs(s[i] - row.scrollLeft) < Math.abs(s[best] - row.scrollLeft)) best = i;
      }
      return best;
    }

    function sync() {
      var max = row.scrollWidth - row.clientWidth - 2;
      if (prev) prev.disabled = row.scrollLeft <= 2;
      if (next) next.disabled = row.scrollLeft >= max;
      var i = currentIndex();
      dots.forEach(function (dot, k) {
        dot.classList.toggle('is-active', k === i);
      });
    }

    function animateTo(target) {
      cancelAnimationFrame(raf);
      var from = row.scrollLeft;
      var delta = target - from;
      var start = null;
      var duration = 380;

      if (prefersReducedMotion()) {
        row.scrollLeft = target;
        sync();
        return;
      }

      row.style.scrollSnapType = 'none';

      function step(ts) {
        if (start === null) start = ts;
        var p = Math.min(1, (ts - start) / duration);
        var eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        row.scrollLeft = from + delta * eased;
        if (p < 1) {
          raf = requestAnimationFrame(step);
        } else {
          row.style.scrollSnapType = '';
          sync();
        }
      }

      raf = requestAnimationFrame(step);
    }

    function go(i) {
      var s = stops();
      animateTo(s[Math.max(0, Math.min(s.length - 1, i))]);
    }

    if (dotsWrap) {
      dotsWrap.innerHTML = '';
      cards.forEach(function (_, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', 'Go to testimonial ' + (i + 1));
        dot.addEventListener('click', function () { go(i); });
        dotsWrap.appendChild(dot);
        dots.push(dot);
      });
    }

    if (prev) prev.addEventListener('click', function () { go(currentIndex() - 1); });
    if (next) next.addEventListener('click', function () { go(currentIndex() + 1); });
    row.addEventListener('scroll', sync);
    window.addEventListener('resize', sync);

    var down = false;
    var startX = 0;
    var startLeft = 0;
    var moved = 0;

    row.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      down = true;
      moved = 0;
      startX = e.clientX;
      startLeft = row.scrollLeft;
      row.classList.add('is-dragging');
    });

    row.addEventListener('pointermove', function (e) {
      if (!down) return;
      var d = e.clientX - startX;
      moved = Math.abs(d);
      row.scrollLeft = startLeft - d;
    });

    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (evt) {
      row.addEventListener(evt, function () {
        if (!down) return;
        down = false;
        row.classList.remove('is-dragging');
        row.style.scrollSnapType = '';
      });
    });

    row.addEventListener('click', function (e) {
      if (moved > 6) {
        e.preventDefault();
        e.stopPropagation();
        moved = 0;
      }
    }, true);

    sync();
  }

  /* ---------- wiring ---------- */

  function init() {
    document.querySelectorAll('[data-rtq-cl]').forEach(function (root) {
      if (root.dataset.rtqReady) return;
      root.dataset.rtqReady = '1';
      initSlider(root);
      initPreviews(root);
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-rtq-play]') : null;
    if (btn) openModal(btn.getAttribute('data-video'), btn.getAttribute('data-name'));
  });

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) pausePreviews();
    else if (!modal || !modal.classList.contains('is-open')) resumePreviews();
  });

  window.rtqClInit = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);
})();
